// Resolvedor único de escopo: parceiro, proprietário e o que o usuário pode.
//
// Antes disto a mesma pergunta ("este usuário é admin geral?") era respondida
// em cinco lugares, cada um com uma lógica diferente, sempre lendo o blob
// localStorage['user'] direto. A camada de parceiro multiplicaria essas cópias.
//
// ATENÇÃO À COMPARAÇÃO COM ZERO. O código antigo infere "admin geral" como
// `!user.userTenant` — e em JavaScript `!0` é `true`. Com a chegada do
// **tenant zero**, que é um tenant de verdade e governa o cadastro de
// parceiros, essa expressão passaria a tratá-lo como "sem proprietário".
// Por isso aqui tudo compara com `null`/`undefined` explicitamente, nunca por
// valor-verdade.

export interface Scope {
  /**
   * Parceiro do usuário. Três valores com significados DIFERENTES:
   *
   * - `null`  → super admin: está acima de qualquer parceiro.
   * - `0`     → operação principal: não tem parceiro terceiro associado.
   * - `> 0`   → parceiro terceiro: só enxerga o próprio grupo.
   *
   * Não colapse null em 0. Foram confirmados como coisas distintas: os super
   * admins vêm com parceiro NULO, e os usuários de parceiro 0 são os da
   * operação principal.
   */
  partnerId: number | null;
  /** Proprietário do usuário, ou null quando ele não tem um associado. */
  tenantId: number | null;
  /** Papel numérico (1 = admin), quando conhecido. */
  userRole: number | null;
  userId: number | null;

  /**
   * O servidor informou o parceiro deste usuário?
   *
   * Falso quando a chave sequer veio na resposta do login — o backend ainda
   * não tem o campo. NÃO confundir com `partnerId === null`, que é o super
   * admin.
   */
  partnerKnown: boolean;
  /** Super admin — sem parceiro associado, acima de todos. */
  isSuperAdmin: boolean;
  /** Operação principal (parceiro 0), sem parceiro terceiro. */
  isMainOperation: boolean;
  /** Pertence a um parceiro terceiro (> 0) e só enxerga aquele grupo. */
  isThirdPartyPartner: boolean;
  /** Super admin OU operação principal — quem vive no centro da plataforma. */
  isRootPartner: boolean;
  /** Não tem proprietário associado — o nível mais alto de administração. */
  isUnscopedAdmin: boolean;
  /** Está no proprietário zero? É ele que governa o cadastro de parceiros. */
  isTenantZero: boolean;

  /** Pode cadastrar/editar parceiros. */
  canManagePartners: boolean;
  /** Pode ativar/desativar um parceiro — só o super admin. */
  canTogglePartnerStatus: boolean;
  /** Pode excluir parceiros — só os direitos mais altos. */
  canDeletePartners: boolean;
  /** Catálogos globais (raças de cavalo, tipos de proprietário). */
  canManageGlobalCatalogs: boolean;
  /** Pode criar outro admin SEM proprietário associado. */
  canCreateUnscopedAdmin: boolean;
  /**
   * Pode gerir dispositivos (câmeras, gravadores): papéis 1 (admin) e 2
   * (manager), e SÓ eles.
   *
   * Antes a regra era uma lista de NEGAÇÃO por nome — "Viewer" e "Alert" —,
   * que liberava qualquer papel fora dela, inclusive um criado depois. O app
   * usava lista de PERMISSÃO por número, e as duas plataformas divergiam: o
   * mesmo usuário podia agir numa e não na outra. Agora é a mesma regra nos
   * dois lados, e papel desconhecido NÃO passa.
   */
  canManageDevices: boolean;
}

/**
 * Lê um id preservando o ZERO.
 *
 * Use no lugar de `a || b || null` e de `!id`. O encadeamento com `||` trata
 * zero como ausente: `0 || null` é `null`, e `!0` é `true`. Enquanto nenhum id
 * valia zero isso passava despercebido; com o proprietário zero e o parceiro
 * zero, passa a esconder dados ou liberar acesso indevido.
 */
export const normalizarId = (...valores: unknown[]): number | null => {
  for (const bruto of valores) {
    if (bruto === null || bruto === undefined || bruto === '') continue;
    const n = typeof bruto === 'number' ? bruto : parseInt(String(bruto), 10);
    if (!Number.isNaN(n)) return n;
  }
  return null;
};

/** Lê um inteiro de qualquer um dos apelidos, preservando o zero. */
const lerInteiro = (obj: any, chaves: string[]): number | null => {
  for (const chave of chaves) {
    const bruto = obj?.[chave];
    if (bruto === null || bruto === undefined || bruto === '') continue;
    const n = typeof bruto === 'number' ? bruto : parseInt(String(bruto), 10);
    if (!Number.isNaN(n)) return n;
  }
  return null;
};

const TENANT_ALIASES = [
  'userTenant',
  'tenantId',
  'tenant_id',
  'TenantId',
  'Tenant_id',
  'TENANT_ID',
];

const PARTNER_ALIASES = [
  'userPartner',
  'partnerId',
  'partner_id',
  'PartnerId',
  'PARTNER_ID',
];

const ROLE_ALIASES = ['userRole', 'roleId', 'role_id'];

/** Lê o usuário logado do localStorage. Fora de componente, de propósito. */
export function getScope(): Scope {
  let user: any = null;
  try {
    const bruto = localStorage.getItem('user');
    user = bruto ? JSON.parse(bruto) : null;
  } catch {
    user = null;
  }

  const tenantId = lerInteiro(user, TENANT_ALIASES);

  // NULO é significativo: é o super admin. Não colapse em 0 (operação
  // principal) — são coisas distintas.
  const partnerId = lerInteiro(user, PARTNER_ALIASES);

  // O campo sequer veio? Então o parceiro é DESCONHECIDO, e isso não pode
  // virar poder.
  //
  // Permissão administrativa falha NEGANDO. Se o backend ainda não informa o
  // parceiro, tratar o desconhecido como super admin daria poder de criar,
  // excluir e ligar/desligar parceiros a qualquer admin por causa de um campo
  // que faltou. (O caminho oposto vale para visibilidade de ALERTA, onde
  // esconder é o erro caro — ali o padrão é mostrar na dúvida.)
  const partnerKnown =
    user !== null &&
    typeof user === 'object' &&
    PARTNER_ALIASES.some((chave) => chave in user);

  // Nome do papel como RESERVA do número, para quem vem só com `Role`.
  // Antes mapeava apenas 'admin'; 'manager' ficava nulo, e isso virou negação
  // de acesso a gestor legítimo quando o número não vinha.
  const nomeDoPapel =
    typeof user?.Role === 'string' ? user.Role.toLowerCase().trim() : null;
  const userRole =
    lerInteiro(user, ROLE_ALIASES) ??
    (nomeDoPapel === 'admin' ? 1 : nomeDoPapel === 'manager' ? 2 : null);

  const isUnscopedAdmin = tenantId === null;
  const isTenantZero = tenantId === 0;
  const ehAdmin = userRole === 1;

  // Parceiro DESCONHECIDO: cai na regra que existia ANTES desta camada.
  //
  // Negar aqui não protegia nada — deixava a administração de parceiros
  // INUTILIZÁVEL onde a API ainda não envia o campo: nenhum admin passava,
  // nem o legítimo. Era o que acontecia em produção, e uma funcionalidade que
  // ninguém pode usar não é uma funcionalidade segura, é uma ausente.
  //
  // A regra anterior: admin SEM proprietário é o admin geral (é o mesmo
  // critério de isUnscopedAdmin e do isGeneralAdmin no UserForm).
  //
  // Isto NÃO dá poder a quem não tinha: um admin DE proprietário continua de
  // fora, porque tem tenant. O que se recupera é exatamente o comportamento
  // que valia antes de a camada de parceiro existir.
  const raizPorAusenciaDeParceiro = !partnerKnown && ehAdmin && isUnscopedAdmin;

  const isSuperAdmin =
    (partnerKnown && partnerId === null) || raizPorAusenciaDeParceiro;
  const isMainOperation = partnerKnown && partnerId === 0;
  const isThirdPartyPartner =
    partnerKnown && partnerId !== null && partnerId > 0;
  const isRootPartner = isSuperAdmin || isMainOperation;

  return {
    partnerId,
    tenantId,
    userRole,
    userId: lerInteiro(user, ['userId', 'id']),
    partnerKnown,
    isSuperAdmin,
    isMainOperation,
    isThirdPartyPartner,
    isRootPartner,
    isUnscopedAdmin,
    isTenantZero,

    // O parceiro conhecido deixou de ser exigência: isRootPartner já embute a
    // reserva para quando o servidor não envia o campo (ver
    // raizPorAusenciaDeParceiro).
    //
    // Administrar parceiros é do CENTRO DA PLATAFORMA: super admin (parceiro
    // nulo) ou operação principal (parceiro 0). Parceiro terceiro (> 0) nunca.
    //
    // Antes esta regra exigia super admin OU proprietário zero, lendo ao pé da
    // letra que "o cadastro mora no tenant zero". Estava errada: a conta de
    // administração real tem parceiro 0 e NENHUM proprietário associado, e
    // ficava sem ver a tela. O eixo certo é o parceiro, não o proprietário.
    canManagePartners: ehAdmin && isRootPartner,
    // "Só quem tinha poder de criar e gerir pode voltar a ativar (e desativar)"
    // — logo, o mesmo grupo que administra.
    canTogglePartnerStatus: ehAdmin && isRootPartner,
    // Excluir é só para quem não tem proprietário associado.
    canDeletePartners: ehAdmin && isUnscopedAdmin,
    // Raças e tipos de proprietário são catálogos do centro da plataforma.
    // Esta NÃO exige parceiro conhecido: é o comportamento atual do sistema, e
    // bloqueá-la deixaria o admin sem os catálogos antes de a API acompanhar.
    canManageGlobalCatalogs: ehAdmin && (!partnerKnown || isRootPartner),
    // Um admin de parceiro só cria admin COM proprietário associado.
    canCreateUnscopedAdmin: ehAdmin && isRootPartner && isUnscopedAdmin,
    canManageDevices: userRole === 1 || userRole === 2,
  };
}

/** Versão para uso dentro de componentes. */
export function useScope(): Scope {
  return getScope();
}

export default useScope;
